import axios from 'axios';
import store from '../store';
import { selectCsrfToken, updateCsrfToken } from '../store/appSlice';

type AxiosReqOnFulfilledInterceptor = Parameters<typeof axios.interceptors.request.use>[0];
type AxiosRespOnRejectedInterceptor = Parameters<typeof axios.interceptors.response.use>[1];

const CSRF_REQUIRED_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];
const CSRF_HEADER_NAME = 'x-csrf-token';
const CSRF_REQUEST_HEADER_NAME = 'X-CSRF-TOKEN';

const resolveBaseUrl = () => {
  const configuredBaseUrl = import.meta.env.VITE_APP_API_DOMAIN as string | undefined;

  if (!configuredBaseUrl) {
    return '';
  }

  // In local Vite dev, a mismatched API host can break session cookie reuse.
  // Falling back to same-origin lets the dev proxy handle upstream routing.
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    try {
      const apiHost = new URL(configuredBaseUrl, window.location.origin).hostname;
      if (apiHost !== window.location.hostname) {
        return '';
      }
    } catch {
      return '';
    }
  }

  return configuredBaseUrl;
};

// =============================================================================
// Inject Store
// =============================================================================
let injectedStore: typeof store;

export const injectStore = (_store: typeof store) => {
  injectedStore = _store;
};

const getCsrfHeaderValue = (headers: any) => {
  if (!headers) {
    return undefined;
  }

  if (typeof headers.get === 'function') {
    return headers.get(CSRF_HEADER_NAME) || headers.get(CSRF_REQUEST_HEADER_NAME);
  }

  return headers[CSRF_HEADER_NAME] || headers[CSRF_REQUEST_HEADER_NAME];
};

// =============================================================================
// Axios configuration
// =============================================================================
const axiosReqOnFulfilInterceptor: AxiosReqOnFulfilledInterceptor = (config) => {
  const { method, url } = config;
  const hasValidUrlProtocol = (urlParam = '') =>
    ['http://', 'https://'].some((protocol) => urlParam.startsWith(protocol));

  if (!injectedStore) {
    return config;
  }

  if (method && CSRF_REQUIRED_METHODS.includes(method.toUpperCase()) && !hasValidUrlProtocol(url)) {
    const csrfToken = selectCsrfToken(injectedStore.getState());

    if (csrfToken) {
      config.headers = config.headers || {};
      Object.assign(config.headers, { [CSRF_REQUEST_HEADER_NAME]: csrfToken });
    }
  }

  return config;
};

// Response interceptor to capture CSRF token from response headers and update store
const axiosRespOnFulfilInterceptor = (response: any) => {
  const csrfToken = getCsrfHeaderValue(response?.headers);

  if (csrfToken && injectedStore) {
    injectedStore.dispatch(updateCsrfToken(csrfToken));
  }

  return response;
};

const axiosRespOnRejectInterceptor: AxiosRespOnRejectedInterceptor = (error: any) => {
  const csrfToken = getCsrfHeaderValue(error?.response?.headers);

  if (csrfToken && injectedStore) {
    injectedStore.dispatch(updateCsrfToken(csrfToken));
  }

  return Promise.reject(error);
};

const axiosClient = axios.create({
  baseURL: resolveBaseUrl(),
  timeout: 30 * 1000,
  responseType: 'json',
  withCredentials: true,
});

axiosClient.interceptors.request.use(axiosReqOnFulfilInterceptor);
axiosClient.interceptors.response.use(axiosRespOnFulfilInterceptor, axiosRespOnRejectInterceptor);

export default axiosClient;
